import { reflow } from 'helpers/dom-helper';
import { waitForTransition } from 'helpers/timing';
import { brandedId } from 'helpers/random-id';

const CLASS_NAME_COLLAPSING = 'collapsing'; // bootstrap's height transition class

export default class AccordionItemElement extends HTMLElement {
  toggleBtn: HTMLElement | null = null;
  panel: HTMLElement | null = null;
  private isTransitioning = false;

  constructor() {
    super();

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  static get observedAttributes(): string[] {
    return ['open', 'disabled'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;

    switch (name) {
      case 'open':
        newValue === null ? this.hide() : this.show();
        break;
      case 'disabled':
        newValue === null ? this.enableElement() : this.disableElement();
        break;
      default:
        break;
    }
  }

  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-accordion-item-toggle-btn]');
    this.panel = this.querySelector('[data-accordion-item-panel]');

    this.initializeElements();
    this.toggleBtn?.addEventListener('click', this.handleClick);
    this.toggleBtn?.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.isTransitioning = false;
    this.toggleBtn?.removeEventListener('click', this.handleClick);
    this.toggleBtn?.removeEventListener('keydown', this.handleKeydown);
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value: boolean) {
    if (value) {
      this.setAttribute('open', '');
      this.toggleBtn?.setAttribute('aria-expanded', 'true');
    } else {
      this.removeAttribute('open');
      this.toggleBtn?.setAttribute('aria-expanded', 'false');
    }
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value: boolean) {
    if (value) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  private handleClick() {
    if (this.disabled) return;
    this.open = !this.open;
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.toggleBtn?.click();
    }
  }

  async show() {
    if (this.isTransitioning || !this.panel) return;

    dispatchEvent(this, 'show');
    this.panel.classList.add(CLASS_NAME_COLLAPSING);
    this.panel.hidden = false;
    this.panel.style.height = '0';
    this.isTransitioning = true;
    this.panel.style.height = `${this.panel.scrollHeight}px`;

    await waitForTransition(this.panel);
    this.isTransitioning = false;
    this.panel.classList.remove(CLASS_NAME_COLLAPSING);
    this.panel.style.height = '';
    dispatchEvent(this, 'shown');
  }

  async hide() {
    if (this.isTransitioning || !this.panel) return;

    dispatchEvent(this, 'hide');
    this.panel.style.height = `${this.panel.getBoundingClientRect().height}px`;
    reflow(this.panel);
    this.panel.classList.add(CLASS_NAME_COLLAPSING);
    this.isTransitioning = true;
    this.panel.style.height = '';

    await waitForTransition(this.panel);
    this.isTransitioning = false;
    this.panel.classList.remove(CLASS_NAME_COLLAPSING);
    this.panel.hidden = true;
    dispatchEvent(this, 'hidden');
  }

  enableElement() {
    if (!this.toggleBtn) return;

    this.toggleBtn.setAttribute('tabindex', '0');
    this.toggleBtn.setAttribute('aria-disabled', 'false');
    this.toggleBtn.removeAttribute('disabled');
  }

  disableElement() {
    if (!this.toggleBtn) return;

    this.toggleBtn.setAttribute('tabindex', '-1');
    this.toggleBtn.setAttribute('aria-disabled', 'true');
    this.toggleBtn.setAttribute('disabled', '');
  }

  private initializeElements() {
    if (!this.panel || !this.toggleBtn) return;

    this.disabled ? this.disableElement() : this.enableElement();
    this.toggleBtn.setAttribute('aria-expanded', this.open.toString());
    this.panel.hidden = !this.open;

    // Don't override user provided `id`.
    if (!this.panel.id) this.panel.id = brandedId();
    this.toggleBtn.setAttribute('aria-controls', this.panel.id);
  }
}

function dispatchEvent<T>(element: HTMLElement, name: string, options: CustomEventInit<T> = {}): boolean {
  return element.dispatchEvent(new CustomEvent(`accordion-item:${name}`, { bubbles: true, ...options }));
}
